import React, { useState } from "react";
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import "./ApiKeysView.css";

import blackForestLogo from "../../assets/blackforest.webp";
import openAILogo from "../../assets/openai.webp";
import togetherLogo from "../../assets/together.webp";

/* ---- proveedores ---- */
const PROVIDERS = [
  { id: "blackForest", tKey: "providers.blackForest", logo: blackForestLogo },
  { id: "openAI", tKey: "providers.openAI", logo: openAILogo },
  { id: "together", tKey: "providers.together", logo: togetherLogo },
];

/* ---- animaciones ---- */
const accordion = {
  closed: { height: 0, opacity: 0, paddingTop: 0, paddingBottom: 0 },
  open: { height: "auto", opacity: 1, paddingTop: 0, paddingBottom: 40 },
};
const spring = { type: "spring", stiffness: 260, damping: 30 };

export default function ApiKeysView() {
  const { t } = useTranslation();

  // Estado principal: lista de proveedores y sus keys con descripción
  const [data, setData] = useState(PROVIDERS.map((p) => ({ ...p, keys: [] })));
  const [openId, setOpenId] = useState(null);
  const [modalId, setModalId] = useState(null);
  const [tempKey, setTempKey] = useState("");
  const [tempDesc, setTempDesc] = useState("");
  const [visible, setVisible] = useState({});

  const toggleAccordion = (id) =>
    setOpenId((prev) => (prev === id ? null : id));

  const toggleVisible = (pid, idx) =>
    setVisible((prev) => ({
      ...prev,
      [`${pid}-${idx}`]: !prev[`${pid}-${idx}`],
    }));

  const openAddModal = (pid) => {
    setModalId(pid);
    setTempKey("");
    setTempDesc("");
  };

  const saveKey = () => {
    if (!tempKey.trim()) return;
    setData((prev) =>
      prev.map((p) => {
        if (p.id !== modalId) return p;
        return {
          ...p,
          keys: [
            ...p.keys,
            { value: tempKey.trim(), description: tempDesc.trim() },
          ],
        };
      })
    );
    // Cerrar modal y limpiar temporal
    setModalId(null);
    setTempKey("");
    setTempDesc("");
  };

  const deleteKey = (pid, idx) =>
    setData((prev) =>
      prev.map((p) =>
        p.id === pid ? { ...p, keys: p.keys.filter((_, i) => i !== idx) } : p
      )
    );

  return (
    <div className="api-keys-view">
      <h1 className="page-title">{t("apiKeys.title")}</h1>
      <p className="page-desc">{t("apiKeys.pageDesc")}</p>

      <div className="providers-list">
        {data.map((provider) => {
          const open = openId === provider.id;
          return (
            <motion.section
              key={provider.id}
              layout
              transition={spring}
              className="provider"
            >
              <button
                className="provider-header"
                onClick={() => toggleAccordion(provider.id)}
                aria-expanded={open}
              >
                <div className="header-left">
                  <img src={provider.logo} alt="" className="logo" />
                  <span className="name">{t(provider.tKey)}</span>
                </div>
                {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key="body"
                    className="provider-body"
                    variants={accordion}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    transition={{ duration: 0.28, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <button
                      className="btn btn-primary"
                      onClick={() => openAddModal(provider.id)}
                    >
                      <Plus size={18} /> {t("apiKeys.addKey")}
                    </button>

                    {provider.keys.length === 0 ? (
                      <p className="empty">{t("apiKeys.noKeys")}</p>
                    ) : (
                      <ul className="keys-list">
                        {provider.keys.map((keyObj, idx) => {
                          const keyId = `${provider.id}-${idx}`;
                          const show = visible[keyId];
                          return (
                            <li key={idx} className="key-row">
                              {/* --- boton visibilidad --- */}
                              <button
                                className="icon-btn eye-btn"
                                onClick={() => toggleVisible(provider.id, idx)}
                                aria-label="toggle visibility"
                              >
                                {show ? <EyeOff size={20} /> : <Eye size={20} />}
                              </button>
                              <span className="key-text">
                                {show
                                  ? keyObj.value
                                  : keyObj.value.replace(/.(?=.{4})/g, "•")}
                              </span>
                              <span className="key-desc">{keyObj.description}</span>

                              {/* --- acción única: eliminar --- */}
                              <div className="key-actions">
                                <button
                                  className="icon-btn delete-btn"
                                  onClick={() => deleteKey(provider.id, idx)}
                                  aria-label="delete key"
                                >
                                  <Trash2 size={20} />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.section>
          );
        })}
      </div>

      {/* --------------- Modal para agregar clave --------------- */}
      <AnimatePresence>
        {modalId && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setModalId(null)}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={spring}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
            >
              <h2 className="modal-title">
                {t("apiKeys.addKeyFor", {
                  provider: t(PROVIDERS.find((p) => p.id === modalId).tKey),
                })}
              </h2>

              {/* Campo pequeño: input para la descripción */}
              <input
                className="input desc-input"
                placeholder={t("apiKeys.descPlaceholder")}
                value={tempDesc}
                onChange={(e) => setTempDesc(e.target.value)}
              />
              {/* Campo grande: textarea para la key */}
              <textarea
                className="input key-input"
                placeholder={t("apiKeys.placeholder")}
                value={tempKey}
                onChange={(e) => setTempKey(e.target.value)}
              />

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setModalId(null)}
                >
                  {t("apiKeys.cancel")}
                </button>
                <button className="btn btn-primary" onClick={saveKey}>
                  {t("apiKeys.save")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
